import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import {
  Plus, X, Save, Pencil, Trash2, Bell, BellOff, Send,
  AlertTriangle, CheckCircle, Clock, TrendingUp, FileText,
  ChevronDown, ChevronUp, ExternalLink, RefreshCw, Mail,
  Search, Link, History, BookOpen, CloudOff, Cloud, Loader,
} from 'lucide-react';

// ── Sheets sync ────────────────────────────────────────────────────────────

const MSO_DATA_URL = '/.netlify/functions/mso-data';

async function loadMSOsFromSheets() {
  try {
    const res = await fetch(MSO_DATA_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'loadMSORegistry' }),
    });
    const data = await res.json();
    if (data.success && Array.isArray(data.msos)) return data.msos;
    return null;
  } catch { return null; }
}

async function saveMSOsToSheets(msos) {
  try {
    const res = await fetch(MSO_DATA_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'saveMSORegistry', msos }),
    });
    const data = await res.json();
    return data.success === true;
  } catch { return false; }
}

// ── Persistence ────────────────────────────────────────────────────────────

const STORAGE_KEY = 'hp-msos';

// Bump when SEED_MSOS is materially revised. On load, an install still holding
// only the previous *untouched* default seed is upgraded to the new set; any
// registry the user has actually edited is left alone.
const SEED_VERSION     = '2026-07-10';
const SEED_VERSION_KEY = 'hp-mso-seed-version';
const OLD_SEED_IDS     = ['mso-001', 'mso-002', 'mso-003'];

function loadMSOs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return SEED_MSOS;
    const stored = JSON.parse(raw);
    const ver = localStorage.getItem(SEED_VERSION_KEY);
    // One-time upgrade: replace the stale fabricated seed with the current
    // instrument set, but only if it was never touched (still the old 3 ids).
    if (ver !== SEED_VERSION && Array.isArray(stored) && stored.length > 0 &&
        stored.every(m => OLD_SEED_IDS.includes(m.id))) {
      return SEED_MSOS;
    }
    return stored;
  } catch { return SEED_MSOS; }
}

function saveMSOs(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(SEED_VERSION_KEY, SEED_VERSION);
  } catch {}
}

// ── History log ────────────────────────────────────────────────────────────

const HISTORY_KEY = 'hp-mso-history';
const REFS_KEY    = 'hp-mso-refs';

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
}
function saveHistory(h) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(0, 200))); } catch {} // cap at 200 events
}
function pushHistory(entry) {
  const h = loadHistory();
  h.unshift({ ...entry, ts: new Date().toISOString() });
  saveHistory(h);
}

function loadRefs() {
  try {
    const raw = localStorage.getItem(REFS_KEY);
    return raw ? JSON.parse(raw) : SEED_REFS;
  } catch { return SEED_REFS; }
}
function saveRefs(r) {
  try { localStorage.setItem(REFS_KEY, JSON.stringify(r)); } catch {}
}

// ── Seed data — Fair Work Commission road transport instruments ────────────
// Status as at 10 July 2026. Business: P H Bouveng Pty Ltd — subcontractor
// drivers (regulated road transport contractors) doing last-mile parcel
// delivery in an Australia Post contractual chain.

const SEED_MSOS = [
  {
    id: 'ms2024-2',
    title: 'MS2024/2 — Road Transport Minimum Standards Order (Last-Mile Delivery)',
    authority: 'Fair Work Commission — Road Transport Expert Panel',
    category: 'Minimum Standards Order',
    referenceNo: 'MS2024/2',
    issuedDate: '',
    effectiveDate: '',
    reviewDate: '',
    status: 'pending',
    primaryExposure: true,
    summary: 'PRIMARY EXPOSURE. Proposed minimum standards order for regulated road transport contractors involved in the transport of goods, wares and other things (other than food, beverages or like items). As at 10 July 2026 this is PENDING — no notice of intent and no draft order have been published. This is the FWC instrument most likely to capture P H Bouveng subcontractor drivers doing last-mile parcel delivery in the Australia Post chain.',
    impacts: [
      'Most likely FWC instrument to directly regulate our subcontractor last-mile drivers.',
      'No compliance obligations yet — pending a notice of intent and a draft order.',
      'If a draft order issues here, the temporary MS2024/3 on-demand order is reviewed within 21 days.',
      'Active watch item: monitor the FWC for any notice of intent or draft order.',
    ],
    actions: [
      { id: 'a1', description: 'Monitor FWC for a notice of intent / draft order in MS2024/2', owner: 'Management', dueDate: '', status: 'in_progress' },
    ],
    banner: { tone: 'amber', text: 'Primary exposure — PENDING. No notice of intent or draft order yet. Watch item only; no compliance obligations at this stage.' },
    sourceUrl: 'https://www.fwc.gov.au/search?query=MS2024%2F2+road+transport+minimum+standards',
    notifyOnUpdate: true,
  },
  {
    id: 'ms2024-1',
    title: 'MS2024/1 — Employee-like Worker Minimum Standards Order (Last-Mile Delivery)',
    authority: 'Fair Work Commission — Road Transport Expert Panel',
    category: 'Minimum Standards Order',
    referenceNo: 'MS2024/1',
    issuedDate: '',
    effectiveDate: '',
    reviewDate: '',
    status: 'monitoring',
    summary: 'Proposed minimum standards order covering employee-like workers moving goods, wares and other things (excluding food and beverages). As at 10 July 2026 this is PENDING — no draft order published. Unlikely to capture our subcontractor drivers, but the coverage boundary between "employee-like worker" and "regulated road transport contractor" is contested.',
    impacts: [
      'Monitor only — unlikely to capture our subcontractor drivers.',
      'Coverage boundary with the road transport contractor stream (MS2024/2) is contested.',
      'No draft order published; no obligations at this stage.',
    ],
    actions: [
      { id: 'b1', description: 'Track FWC for a draft order in MS2024/1', owner: 'Management', dueDate: '', status: 'in_progress' },
    ],
    banner: { tone: 'grey', text: 'PENDING — monitor only. No draft order. Unlikely to cover our subbies; coverage boundary is contested.' },
    sourceUrl: 'https://www.fwc.gov.au/search?query=MS2024%2F1+employee-like+minimum+standards',
    notifyOnUpdate: true,
  },
  {
    id: 'ms2024-3',
    title: 'MS2024/3 — On-Demand Delivery Employee-like Worker Minimum Standards Order',
    authority: 'Fair Work Commission — Expert Panel',
    category: 'Minimum Standards Order',
    referenceNo: 'MS2024/3',
    issuedDate: '',
    effectiveDate: '2026-08-10',
    reviewDate: '',
    status: 'out_of_scope',
    summary: 'NOT APPLICABLE TO US. Decision and draft order published. Covers on-demand food, beverage, liquor or grocery delivery via a digital labour platform — not last-mile parcel delivery in a contractual chain. Kept visible because it is the trigger event for the MS2024/1 and MS2024/2 timelines: the order is intended to be temporary and would be reviewed within 21 days of a notice of intent and draft order being published in MS2024/1 or MS2024/2.',
    impacts: [
      'Out of scope — on-demand food / beverage / liquor / grocery via a digital platform, not our operations.',
      'Submissions on the draft order due 4:00pm AEST Wednesday 29 July 2026.',
      'If an order is made on the draft, it would commence 10 August 2026 (temporary).',
      'Trigger to watch: its review clock is what starts the MS2024/1 & /2 timelines.',
    ],
    actions: [
      { id: 'c1', description: 'Note submissions deadline (29 Jul 2026) — no submission required (out of scope)', owner: 'Management', dueDate: '2026-07-29', status: 'pending' },
      { id: 'c2', description: 'Watch for order commencement 10 Aug 2026 — trigger for MS2024/1 & /2 review', owner: 'Management', dueDate: '2026-08-10', status: 'pending' },
    ],
    compliance: {
      applicability: 'not_applicable',
      applicabilityNotes: 'Covers on-demand food, beverage, liquor or grocery delivery via a digital labour platform. P H Bouveng performs last-mile parcel delivery within an Australia Post contractual chain — not on-demand platform food/grocery delivery. Out of scope.',
    },
    banner: { tone: 'grey', text: 'Out of scope — on-demand food/grocery platforms. Kept visible: it is the trigger event for the MS2024/1 & /2 timelines. Submissions due 29 Jul 2026; possible commencement 10 Aug 2026.' },
    sourceUrl: 'https://www.fwc.gov.au/search?query=MS2024%2F3+on-demand+delivery+minimum+standards',
    notifyOnUpdate: true,
  },
  {
    id: 'rtcco-2026',
    title: 'RTCCO — Road Transport Contractual Chain Fuel Cost Recovery Order 2026',
    authority: 'Fair Work Commission — Road Transport Expert Panel',
    category: 'Contractual Chain Order',
    referenceNo: 'RTCCO 2026',
    issuedDate: '',
    effectiveDate: '2026-04-21',
    reviewDate: '2026-08-26',
    status: 'suspended',
    clause4Active: false,
    summary: 'LIVE, obligations dormant. The Order operates from 21 April 2026 and covers road transport contractual chains (excluding cash in transit). On 29 May 2026 the Expert Panel stated the Order will not be varied as proposed, and — due to the recent fall in the wholesale price of diesel — the fuel cost-recovery obligations in clause 4 no longer apply. Trigger: the weekly average national terminal gate price for diesel fell below $2.00/L for the week ending 5 June 2026 (Australian Institute of Petroleum, reported 7 June 2026). A report-back and case management hearing is listed for 10:00am AEST on 26 August 2026 as part of the three-month review.',
    impacts: [
      'Clause 4 fuel pass-through obligations are SUSPENDED (clause4Active: false) — do not calculate or pass through while dormant.',
      'Do NOT remove the fuel pass-through logic — it reactivates if diesel rises back above the trigger.',
      'Keep fortnightly diesel price tracking running: it is both the reactivation trigger and the audit trail.',
      'Reactivation trigger: weekly average national terminal gate diesel price returning above $2.00/L (AIP).',
      'Review checkpoint: report-back & case management hearing, 10am AEST 26 August 2026.',
    ],
    actions: [
      { id: 'd1', description: 'Confirm Fuel Workbench pass-through is gated off while clause 4 dormant (clause4Active:false)', owner: 'Operations', dueDate: '', status: 'pending' },
      { id: 'd2', description: 'Continue fortnightly diesel terminal gate price tracking (reactivation trigger + audit trail)', owner: 'Operations', dueDate: '', status: 'in_progress' },
      { id: 'd3', description: 'Review outcome of report-back hearing (26 Aug 2026)', owner: 'Management', dueDate: '2026-08-26', status: 'pending' },
    ],
    banner: { tone: 'amber', text: 'Clause 4 fuel obligations SUSPENDED (diesel below $2.00/L, week ending 5 Jun 2026). Monitoring continues; do not remove pass-through logic. Review: 26 Aug 2026.' },
    sourceUrl: 'https://www.fwc.gov.au/search?query=road+transport+contractual+chain+fuel+cost+recovery+order',
    notifyOnUpdate: true,
  },
];

// ── Reference links seed data ──────────────────────────────────────────────

const SEED_REFS = [
  { id: 'ref-001', name: 'FWC — Road Transport Minimum Standards', url: 'https://www.fwc.gov.au/search?query=road+transport+minimum+standards', category: 'Legislation', description: 'FWC road transport minimum standards proceedings — MS2024/1 & MS2024/2' },
  { id: 'ref-011', name: 'FWC — RTCCO Fuel Cost Recovery Order', url: 'https://www.fwc.gov.au/search?query=road+transport+contractual+chain+fuel+cost+recovery', category: 'Legislation', description: 'Road Transport Contractual Chain (fuel cost recovery) Order 2026 — clause 4 status' },
  { id: 'ref-012', name: 'Australian Institute of Petroleum — TGP', url: 'https://www.aip.com.au/pricing/terminal-gate-prices', category: 'Industry', description: 'Weekly terminal gate diesel prices — RTCCO clause 4 reactivation trigger (< $2.00/L)' },
  { id: 'ref-002', name: 'FWC Decision Search — MSO', url: 'https://www.fwc.gov.au/search?query=minimum+standards+order', category: 'Legislation', description: 'Search all FWC decisions for new minimum standards orders' },
  { id: 'ref-003', name: 'Federal Register of Legislation', url: 'https://www.legislation.gov.au', category: 'Legislation', description: 'Official Australian Government database for Acts and legislative instruments' },
  { id: 'ref-004', name: 'Dept. of Employment & Workplace Relations', url: 'https://www.dewr.gov.au/independent-contractors', category: 'Legislation', description: 'Federal department overseeing gig economy reform and workplace relations policy' },
  { id: 'ref-005', name: 'Fair Work Ombudsman', url: 'https://www.fairwork.gov.au', category: 'Regulatory', description: 'Advice and guidance on workplace laws, contractor obligations and compliance' },
  { id: 'ref-006', name: 'NHVR — Chain of Responsibility', url: 'https://www.nhvr.gov.au/safety-accreditation-compliance/chain-of-responsibility', category: 'Transport Safety', description: 'NHVR guidance on Chain of Responsibility obligations under Heavy Vehicle National Law' },
  { id: 'ref-007', name: 'Safe Work Australia', url: 'https://www.safeworkaustralia.gov.au', category: 'WHS', description: 'National policy development for work health and safety and workers compensation' },
  { id: 'ref-008', name: 'Transport Workers Union NSW', url: 'https://www.twunsw.com.au/news', category: 'Industry', description: 'TWU NSW news — source for GCCD rate gazettal and transport worker advocacy' },
  { id: 'ref-009', name: 'Transport for NSW', url: 'https://www.transport.nsw.gov.au', category: 'State Authority', description: 'NSW state transport regulations, licensing, and compliance requirements' },
  { id: 'ref-010', name: 'ATO — Independent Contractors', url: 'https://www.ato.gov.au/businesses-and-organisations/hiring-and-paying-your-workers/contractors', category: 'Tax & Super', description: 'ATO guidance on contractor obligations, GST, and superannuation guarantee' },
];

// ── Sources opened by the auto-search button ───────────────────────────────

const SEARCH_SOURCES = [
  { name: 'FWC — Road Transport Minimum Standards', url: 'https://www.fwc.gov.au/search?query=road+transport+minimum+standards', description: 'MS2024/1 & MS2024/2 — notices of intent and draft orders appear here' },
  { name: 'FWC — RTCCO Fuel Cost Recovery', url: 'https://www.fwc.gov.au/search?query=road+transport+contractual+chain+fuel+cost+recovery', description: 'RTCCO clause 4 status and the 26 Aug 2026 report-back hearing' },
  { name: 'FWC Decision Search', url: 'https://www.fwc.gov.au/search?query=minimum+standards+order', description: 'Full-text search across all FWC decisions and orders' },
  { name: 'Federal Register of Legislation', url: 'https://www.legislation.gov.au/Search/minimum%20standards%20order', description: 'Official gazette — new instruments appear here on registration' },
  { name: 'AIP — Terminal Gate Prices', url: 'https://www.aip.com.au/pricing/terminal-gate-prices', description: 'Weekly diesel TGP — RTCCO reactivation trigger (returning above $2.00/L)' },
  { name: 'Google — Road transport MSO (past month)', url: 'https://www.google.com/search?q=%22minimum+standards%22+road+transport+Fair+Work+Commission&tbs=qdr:m', description: 'Broad news search for any newly published road transport orders' },
];

// ── Compliance data structure & helpers ───────────────────────────────────

const COMPLIANCE_DEFAULT = {
  vehicles:   { microCar: false, smallVan: false, mediumVan: false, largeVan: false, heavyVehicle: false },
  workers:    { independentContractors: false, gigAppBased: false, employees: false },
  operations: { parcelDelivery: false, courierOnDemand: false, depotOperations: false, longDistance: false, chainOfResponsibility: false },
  applicability: '',       // 'applicable' | 'partial' | 'not_applicable'
  applicabilityNotes: '',
  checklist: {
    reviewed: false, assessed: false, contractsUpdated: false,
    ratesVerified: false, recordKeepingAddressed: false,
    personnelNotified: false, policiesUpdated: false, auditCompleted: false,
  },
  signedOffBy: '', signedOffDate: '', nextReviewDate: '', notes: '',
};

function getComplianceOutcome(compliance) {
  if (!compliance?.applicability) return 'pending';
  if (compliance.applicability === 'not_applicable')
    return (compliance.signedOffBy && compliance.signedOffDate) ? 'not_applicable_addressed' : 'not_applicable_pending';
  const items = Object.values(compliance.checklist || {});
  const done = items.filter(Boolean).length;
  if (done === items.length && compliance.signedOffBy && compliance.signedOffDate) return 'compliant';
  if (done > 0 || compliance.applicability) return 'in_progress';
  return 'pending';
}

const COMP_OUTCOME_CFG = {
  pending:                  { label: 'Pending Assessment',          bg: '#1a1a24', color: '#8888a0' },
  not_applicable_addressed: { label: 'Not Applicable — Addressed',  bg: '#083344', color: '#67e8f9' },
  not_applicable_pending:   { label: 'Not Applicable — Unsigned',   bg: '#431407', color: '#fcd34d' },
  in_progress:              { label: 'Assessment In Progress',      bg: '#451a03', color: '#fdba74' },
  compliant:                { label: 'Compliant — Signed Off',      bg: '#14532d', color: '#86efac' },
};

// ── Style helpers ─────────────────────────────────────────────────────────

const S = {
  page: { padding: '28px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 28 },
  title: { fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--hp-text)', letterSpacing: '0.05em' },
  sub: { fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4 },
  card: { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 20 },
  label: { fontSize: '0.68rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 5 },
  input: { width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', padding: '8px 12px', fontSize: '0.85rem', fontFamily: 'var(--font-body)' },
  textarea: { width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', padding: '8px 12px', fontSize: '0.85rem', fontFamily: 'var(--font-body)', resize: 'vertical' },
  btn: (bg, color = '#000') => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 'var(--radius)', border: 'none', background: bg, color, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }),
  row: { display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  section: { marginBottom: 16 },
};

// ── Status config ─────────────────────────────────────────────────────────

const STATUS_CFG = {
  pending:      { label: 'Pending — Watch', bg: '#422006', color: '#fcd34d' },
  monitoring:   { label: 'Monitoring',      bg: '#1e3a5f', color: '#93c5fd' },
  active:       { label: 'Active',          bg: '#7f1d1d', color: '#fca5a5' },
  implementing: { label: 'Implementing',    bg: '#78350f', color: '#fcd34d' },
  suspended:    { label: 'Live · Suspended', bg: '#3f3f46', color: '#d4d4d8' },
  out_of_scope: { label: 'Out of Scope',    bg: '#1f2937', color: '#9ca3af' },
  compliant:    { label: 'Compliant',       bg: '#14532d', color: '#86efac' },
};

// ── Per-MSO alert banner ───────────────────────────────────────────────────

const BANNER_TONE = {
  red:   { bg: '#450a0a', border: 'var(--hp-red)',   color: '#fca5a5' },
  amber: { bg: '#431407', border: 'var(--hp-amber)', color: '#fcd34d' },
  grey:  { bg: '#1f2937', border: '#374151',         color: '#cbd5e1' },
};

function MSOBanner({ banner, onClick }) {
  if (!banner) return null;
  const t = BANNER_TONE[banner.tone] || BANNER_TONE.grey;
  return (
    <div onClick={onClick} style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 'var(--radius-lg)', padding: '12px 16px', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 10, cursor: onClick ? 'pointer' : 'default' }}>
      <AlertTriangle size={16} color={t.border} style={{ flexShrink: 0, marginTop: 1 }} />
      <span style={{ fontSize: '0.83rem', color: t.color, lineHeight: 1.5 }}>{banner.text}</span>
    </div>
  );
}

const ACTION_CFG = {
  pending:     { label: 'Pending',     color: '#94a3b8' },
  in_progress: { label: 'In Progress', color: '#fcd34d' },
  completed:   { label: 'Completed',   color: '#86efac' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: 'var(--hp-muted)', color: 'var(--hp-dim)' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700, background: cfg.bg, color: cfg.color, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {cfg.label}
    </span>
  );
}

function ActionBadge({ status }) {
  const cfg = ACTION_CFG[status] || { label: status, color: 'var(--hp-dim)' };
  return <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, fontFamily: 'var(--font-mono)' }}>{cfg.label}</span>;
}

function ComplianceStatusBadge({ compliance }) {
  const outcome = getComplianceOutcome(compliance);
  const cfg = COMP_OUTCOME_CFG[outcome] || COMP_OUTCOME_CFG.pending;
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.68rem', fontWeight: 700, background: cfg.bg, color: cfg.color, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {cfg.label}
    </span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ ...S.card, borderTop: `3px solid ${color}`, display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: '1.6rem', fontWeight: 800, color, fontFamily: 'var(--font-display)', letterSpacing: '0.05em' }}>{value}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      </div>
    </div>
  );
}

// ── Tab bar ──────────────────────────────────────────────────────────────

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--hp-border)', marginBottom: 24 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: '10px 20px', border: 'none', borderRadius: '8px 8px 0 0',
          background: active === t.id ? 'var(--hp-card)' : 'transparent',
          color: active === t.id ? 'var(--hp-gold)' : 'var(--hp-dim)',
          fontWeight: active === t.id ? 700 : 500,
          fontFamily: 'var(--font-body)', fontSize: '0.85rem', cursor: 'pointer',
          borderBottom: active === t.id ? '2px solid var(--hp-gold)' : '2px solid transparent',
          transition: 'all 0.15s',
        }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Editable text field helper ────────────────────────────────────────────

function Field({ label, value, onChange, type = 'text', multiline, rows = 3 }) {
  return (
    <div style={S.section}>
      <label style={S.label}>{label}</label>
      {multiline
        ? <textarea rows={rows} style={S.textarea} value={value} onChange={e => onChange(e.target.value)} />
        : <input type={type} style={S.input} value={value} onChange={e => onChange(e.target.value)} />
      }
    </div>
  );
}

// ── Search modal ──────────────────────────────────────────────────────────

function SearchModal({ onClose, onSearchDone }) {
  const [opened, setOpened] = useState([]);
  const [searching, setSearching] = useState(false);

  const openSource = (src) => {
    window.open(src.url, '_blank', 'noopener,noreferrer');
    setOpened(o => o.includes(src.url) ? o : [...o, src.url]);
  };

  const handleOpenAll = () => {
    setSearching(true);
    SEARCH_SOURCES.forEach((src, i) => {
      setTimeout(() => {
        window.open(src.url, '_blank', 'noopener,noreferrer');
        setOpened(o => [...o, src.url]);
        if (i === SEARCH_SOURCES.length - 1) setSearching(false);
      }, i * 400);
    });
    onSearchDone();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--hp-dark)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 640, padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-gold)' }}>SEARCH FOR NEW MSOs</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 20, lineHeight: 1.6 }}>
          Opens the official government and industry sources where new MSOs are gazetted. Review each for any new orders relevant to your operations, then add them to the registry.
        </p>

        <div style={{ marginBottom: 20 }}>
          {SEARCH_SOURCES.map(src => (
            <div key={src.url} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--hp-border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)', fontWeight: 600 }}>{src.name}</span>
                  {opened.includes(src.url) && <CheckCircle size={13} color="var(--hp-green)" />}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>{src.description}</div>
              </div>
              <button onClick={() => openSource(src)} style={S.btn('var(--hp-surface)', 'var(--hp-gold)')}>
                <ExternalLink size={13} /> Open
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={S.btn('var(--hp-surface)', 'var(--hp-dim)')}>Close</button>
          <button onClick={handleOpenAll} disabled={searching} style={{ ...S.btn('var(--hp-gold)', '#000'), opacity: searching ? 0.7 : 1 }}>
            <Search size={14} /> {searching ? 'Opening sources…' : 'Open All Sources'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add / Edit MSO modal ───────────────────────────────────────────────────

const EMPTY_MSO = {
  id: '', title: '', authority: '', category: '', referenceNo: '',
  issuedDate: '', effectiveDate: '', reviewDate: '', status: 'monitoring',
  summary: '', impacts: [''], actions: [], sourceUrl: '', notifyOnUpdate: true,
};

function MSOModal({ mso, onSave, onClose }) {
  const [form, setForm] = useState(mso ? { ...mso, impacts: mso.impacts.length ? mso.impacts : [''] } : { ...EMPTY_MSO, id: 'mso-' + Date.now() });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setImpact = (i, v) => set('impacts', form.impacts.map((x, idx) => idx === i ? v : x));
  const addImpact = () => set('impacts', [...form.impacts, '']);
  const removeImpact = i => set('impacts', form.impacts.filter((_, idx) => idx !== i));

  const handleSave = () => {
    if (!form.title.trim()) return;
    onSave({ ...form, impacts: form.impacts.filter(x => x.trim()) });
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--hp-dark)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-gold)' }}>{mso ? 'EDIT MSO' : 'ADD NEW MSO'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={S.grid2}>
          <Field label="MSO Title" value={form.title} onChange={v => set('title', v)} />
          <Field label="Issuing Authority" value={form.authority} onChange={v => set('authority', v)} />
        </div>
        <div style={S.grid2}>
          <Field label="Category" value={form.category} onChange={v => set('category', v)} />
          <Field label="Reference Number" value={form.referenceNo} onChange={v => set('referenceNo', v)} />
        </div>
        <div style={S.grid3}>
          <Field label="Issued Date" type="date" value={form.issuedDate} onChange={v => set('issuedDate', v)} />
          <Field label="Effective Date" type="date" value={form.effectiveDate} onChange={v => set('effectiveDate', v)} />
          <Field label="Review Date" type="date" value={form.reviewDate} onChange={v => set('reviewDate', v)} />
        </div>
        <div style={S.section}>
          <label style={S.label}>Status</label>
          <select style={S.input} value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="pending">Pending — Watch</option>
            <option value="monitoring">Monitoring</option>
            <option value="active">Active</option>
            <option value="implementing">Implementing</option>
            <option value="suspended">Live · Suspended</option>
            <option value="out_of_scope">Out of Scope</option>
            <option value="compliant">Compliant</option>
          </select>
        </div>
        <Field label="Summary / Description" value={form.summary} onChange={v => set('summary', v)} multiline rows={3} />

        <div style={S.section}>
          <label style={S.label}>Business Impacts</label>
          {form.impacts.map((imp, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input style={{ ...S.input, flex: 1 }} value={imp} onChange={e => setImpact(i, e.target.value)} placeholder="Describe this impact..." />
              {form.impacts.length > 1 && (
                <button onClick={() => removeImpact(i)} style={{ background: 'none', border: 'none', color: 'var(--hp-red)', cursor: 'pointer' }}><X size={16} /></button>
              )}
            </div>
          ))}
          <button onClick={addImpact} style={S.btn('var(--hp-surface)', 'var(--hp-gold)')}>
            <Plus size={14} /> Add Impact
          </button>
        </div>

        <Field label="Source URL" value={form.sourceUrl} onChange={v => set('sourceUrl', v)} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <input type="checkbox" id="notifyChk" checked={form.notifyOnUpdate} onChange={e => set('notifyOnUpdate', e.target.checked)} style={{ cursor: 'pointer' }} />
          <label htmlFor="notifyChk" style={{ fontSize: '0.85rem', color: 'var(--hp-text)', cursor: 'pointer' }}>Notify subscribed personnel when this MSO is updated</label>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={S.btn('var(--hp-surface)', 'var(--hp-dim)')}>Cancel</button>
          <button onClick={handleSave} style={S.btn('var(--hp-gold)', '#000')}><Save size={14} /> Save MSO</button>
        </div>
      </div>
    </div>
  );
}

// ── Action item row ───────────────────────────────────────────────────────

function ActionRow({ action, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(action);

  const handleSave = () => { onUpdate(form); setEditing(false); };

  if (editing) {
    return (
      <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid var(--hp-gold)' }}>
        <div style={S.grid2}>
          <input style={S.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Action description" />
          <input style={S.input} value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} placeholder="Owner / Team" />
        </div>
        <div style={{ ...S.row, marginTop: 8 }}>
          <input type="date" style={{ ...S.input, flex: 1 }} value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
          <select style={{ ...S.input, flex: 1 }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={handleSave} style={S.btn('var(--hp-green)', '#000')}><Save size={13} /></button>
          <button onClick={() => setEditing(false)} style={S.btn('var(--hp-surface)', 'var(--hp-dim)')}><X size={13} /></button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--hp-surface)', borderRadius: 8, marginBottom: 6 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.85rem', color: action.status === 'completed' ? 'var(--hp-dim)' : 'var(--hp-text)', textDecoration: action.status === 'completed' ? 'line-through' : 'none' }}>
          {action.description}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>
          {action.owner} · Due {action.dueDate || 'TBD'}
        </div>
      </div>
      <ActionBadge status={action.status} />
      <button onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><Pencil size={13} /></button>
    </div>
  );
}

// ── Compliance check tab ──────────────────────────────────────────────────

const VEHICLE_ITEMS = [
  { key: 'microCar',     label: 'Micro / Car',                  note: 'Light vehicle' },
  { key: 'smallVan',     label: 'Small Van',                    note: 'Light vehicle' },
  { key: 'mediumVan',    label: 'Medium Van',                   note: 'Light vehicle' },
  { key: 'largeVan',     label: 'Large Van',                    note: 'Light vehicle — sub 4.5t GVM' },
  { key: 'heavyVehicle', label: 'Heavy Vehicle (>4.5t GVM)',    note: 'NOT in our fleet', notInFleet: true },
];
const WORKER_ITEMS = [
  { key: 'independentContractors', label: 'Independent Contractors (Owner-Drivers)' },
  { key: 'gigAppBased',            label: 'App-based / Platform / Gig Workers' },
  { key: 'employees',              label: 'Employees (Direct Employment)' },
];
const OPS_ITEMS = [
  { key: 'parcelDelivery',        label: 'Parcel / Same-Day Delivery' },
  { key: 'courierOnDemand',       label: 'Courier / On-Demand Delivery' },
  { key: 'depotOperations',       label: 'Depot Operations' },
  { key: 'longDistance',          label: 'Long-Distance / Interstate Operations' },
  { key: 'chainOfResponsibility', label: 'Chain of Responsibility (scheduling / consigning)' },
];
const CHECKLIST_ITEMS = [
  { key: 'reviewed',               label: 'MSO reviewed and understood by management' },
  { key: 'assessed',               label: 'Assessed against current operational procedures' },
  { key: 'contractsUpdated',       label: 'Contractor agreements reviewed / updated if required' },
  { key: 'ratesVerified',          label: 'Pay / rate schedules verified against MSO requirements' },
  { key: 'recordKeepingAddressed', label: 'Record-keeping obligations identified and addressed' },
  { key: 'personnelNotified',      label: 'Contractors / personnel notified where required' },
  { key: 'policiesUpdated',        label: 'Policies and procedures updated if required' },
  { key: 'auditCompleted',         label: 'Internal compliance audit / sign-off completed' },
];

function ScopeCheckbox({ checked, onChange, label, note, notInFleet }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: notInFleet ? 'rgba(239,68,68,0.05)' : 'var(--hp-surface)', borderRadius: 6, marginBottom: 6, border: notInFleet ? '1px solid rgba(239,68,68,0.2)' : '1px solid transparent' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ cursor: 'pointer' }} />
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: '0.85rem', color: notInFleet ? 'var(--hp-dim)' : 'var(--hp-text)' }}>{label}</span>
        {note && <span style={{ fontSize: '0.72rem', color: notInFleet ? 'var(--hp-red)' : 'var(--hp-dim)', marginLeft: 8 }}>({note})</span>}
      </div>
      {notInFleet && <span style={{ fontSize: '0.65rem', color: 'var(--hp-red)', fontFamily: 'var(--font-mono)', fontWeight: 700, flexShrink: 0 }}>NOT IN FLEET</span>}
    </div>
  );
}

function ComplianceCheckTab({ mso, compliance, onChange }) {
  const c = { ...COMPLIANCE_DEFAULT, ...compliance, checklist: { ...COMPLIANCE_DEFAULT.checklist, ...(compliance?.checklist || {}) } };
  const set = (k, v) => onChange({ ...c, [k]: v });
  const setVehicle = (k, v) => onChange({ ...c, vehicles: { ...c.vehicles, [k]: v } });
  const setWorker  = (k, v) => onChange({ ...c, workers:  { ...c.workers,  [k]: v } });
  const setOp      = (k, v) => onChange({ ...c, operations: { ...c.operations, [k]: v } });
  const setCheck   = (k, v) => onChange({ ...c, checklist: { ...c.checklist, [k]: v } });

  const checklistDone  = Object.values(c.checklist).filter(Boolean).length;
  const checklistTotal = Object.values(c.checklist).length;
  const showChecklist  = c.applicability === 'applicable' || c.applicability === 'partial';
  const outcome        = getComplianceOutcome(c);
  const cfg            = COMP_OUTCOME_CFG[outcome] || COMP_OUTCOME_CFG.pending;

  const APPLICABILITY_OPTIONS = [
    { value: 'applicable',     label: 'Fully Applicable',                    desc: 'This MSO applies to our operations and must be complied with',     color: '#ef4444' },
    { value: 'partial',        label: 'Partially Applicable',                desc: 'Some aspects of this MSO apply to our operations',                  color: '#f59e0b' },
    { value: 'not_applicable', label: 'Not Applicable to Our Operations',    desc: 'This MSO does not apply based on our scope assessment above',       color: '#22c55e' },
  ];

  return (
    <div>
      {/* Outcome banner */}
      <div style={{ ...S.card, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `4px solid ${cfg.color}` }}>
        <ComplianceStatusBadge compliance={c} />
        <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', flex: 1 }}>
          {outcome === 'pending'                  && 'Complete the assessment below and sign off to close this MSO.'}
          {outcome === 'not_applicable_addressed' && `Confirmed not applicable to our operations. Signed off by ${c.signedOffBy} on ${c.signedOffDate}.`}
          {outcome === 'not_applicable_pending'   && 'Marked not applicable — add a sign-off below to close this assessment.'}
          {outcome === 'in_progress'              && `Compliance checklist: ${checklistDone} of ${checklistTotal} items complete.`}
          {outcome === 'compliant'                && `All items complete. Signed off by ${c.signedOffBy} on ${c.signedOffDate}.`}
        </span>
      </div>

      {/* Section 1 — Scope */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
          1 · Scope Assessment — Does this MSO apply to…
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Fleet — Vehicle Classes</div>
          {VEHICLE_ITEMS.map(v => <ScopeCheckbox key={v.key} checked={c.vehicles[v.key]} onChange={val => setVehicle(v.key, val)} label={v.label} note={v.note} notInFleet={v.notInFleet} />)}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Worker / Engagement Type</div>
          {WORKER_ITEMS.map(w => <ScopeCheckbox key={w.key} checked={c.workers[w.key]} onChange={val => setWorker(w.key, val)} label={w.label} />)}
        </div>

        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Operational Scope</div>
          {OPS_ITEMS.map(o => <ScopeCheckbox key={o.key} checked={c.operations[o.key]} onChange={val => setOp(o.key, val)} label={o.label} />)}
        </div>
      </div>

      {/* Section 2 — Applicability decision */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
          2 · Applicability Decision
        </div>
        {APPLICABILITY_OPTIONS.map(opt => (
          <div key={opt.value} onClick={() => set('applicability', opt.value)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 8, marginBottom: 8, cursor: 'pointer', border: `2px solid ${c.applicability === opt.value ? opt.color : 'var(--hp-border)'}`, background: c.applicability === opt.value ? opt.color + '18' : 'var(--hp-surface)', transition: 'all 0.15s' }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${opt.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
              {c.applicability === opt.value && <div style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color }} />}
            </div>
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: c.applicability === opt.value ? opt.color : 'var(--hp-text)' }}>{opt.label}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>{opt.desc}</div>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          <label style={S.label}>Reason / Notes{c.applicability === 'not_applicable' ? ' (required)' : ' (optional)'}</label>
          <textarea rows={3} style={S.textarea} value={c.applicabilityNotes} onChange={e => set('applicabilityNotes', e.target.value)}
            placeholder={c.applicability === 'not_applicable'
              ? 'e.g. We operate light vehicles only — Micro/Car, Small Van, Medium Van, Large Van (all sub 4.5t GVM). Heavy Vehicle National Law does not apply to our fleet.'
              : 'Add context or reason for this applicability assessment...'} />
        </div>
      </div>

      {/* Section 3 — Compliance checklist (only when applicable/partial) */}
      {showChecklist && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>3 · Compliance Checklist</div>
            <div style={{ fontSize: '0.75rem', color: checklistDone === checklistTotal ? 'var(--hp-green)' : 'var(--hp-dim)', fontWeight: 600 }}>{checklistDone}/{checklistTotal} complete</div>
          </div>
          {CHECKLIST_ITEMS.map(item => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--hp-surface)', borderRadius: 6, marginBottom: 6, border: `1px solid ${c.checklist[item.key] ? 'rgba(34,197,94,0.25)' : 'transparent'}` }}>
              <input type="checkbox" checked={c.checklist[item.key]} onChange={e => setCheck(item.key, e.target.checked)} style={{ cursor: 'pointer' }} />
              <span style={{ flex: 1, fontSize: '0.85rem', color: c.checklist[item.key] ? 'var(--hp-dim)' : 'var(--hp-text)', textDecoration: c.checklist[item.key] ? 'line-through' : 'none' }}>{item.label}</span>
              {c.checklist[item.key] && <CheckCircle size={14} color="var(--hp-green)" style={{ flexShrink: 0 }} />}
            </div>
          ))}
        </div>
      )}

      {/* Section 4 — Sign-off */}
      <div style={{ ...S.card, border: (c.signedOffBy && c.signedOffDate) ? '1px solid rgba(34,197,94,0.35)' : '1px solid var(--hp-border)' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
          {showChecklist ? '4' : '3'} · Sign-Off
        </div>
        <div style={S.grid2}>
          <div style={S.section}>
            <label style={S.label}>Reviewed & Signed Off By</label>
            <input style={S.input} value={c.signedOffBy} onChange={e => set('signedOffBy', e.target.value)} placeholder="Full name" />
          </div>
          <div style={S.section}>
            <label style={S.label}>Date Signed Off</label>
            <input type="date" style={S.input} value={c.signedOffDate} onChange={e => set('signedOffDate', e.target.value)} />
          </div>
        </div>
        <div style={S.grid2}>
          <div style={S.section}>
            <label style={S.label}>Next Review Date</label>
            <input type="date" style={S.input} value={c.nextReviewDate} onChange={e => set('nextReviewDate', e.target.value)} />
          </div>
          <div style={S.section}>
            <label style={S.label}>Additional Notes</label>
            <input style={S.input} value={c.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional compliance notes..." />
          </div>
        </div>
        {c.signedOffBy && c.signedOffDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '8px 12px', background: 'rgba(34,197,94,0.08)', borderRadius: 6, border: '1px solid rgba(34,197,94,0.25)' }}>
            <CheckCircle size={14} color="var(--hp-green)" />
            <span style={{ fontSize: '0.82rem', color: 'var(--hp-green)' }}>Signed off by {c.signedOffBy} on {c.signedOffDate}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MSO Detail panel ──────────────────────────────────────────────────────

function MSODetail({ mso, onBack, onEdit, onUpdateActions, onUpdateCompliance, onToggleClause4 }) {
  const [detailTab, setDetailTab] = useState('overview');
  const [showAddAction, setShowAddAction] = useState(false);
  const [newAction, setNewAction] = useState({ description: '', owner: '', dueDate: '', status: 'pending' });

  const completedCount = mso.actions.filter(a => a.status === 'completed').length;
  const compliance = mso.compliance || COMPLIANCE_DEFAULT;

  const handleAddAction = () => {
    if (!newAction.description.trim()) return;
    onUpdateActions(mso.id, [...mso.actions, { ...newAction, id: 'a-' + Date.now() }]);
    setNewAction({ description: '', owner: '', dueDate: '', status: 'pending' });
    setShowAddAction(false);
  };

  const handleUpdateAction = (updated) => {
    onUpdateActions(mso.id, mso.actions.map(a => a.id === updated.id ? updated : a));
  };

  const DETAIL_TABS = [
    { id: 'overview',   label: 'Overview' },
    { id: 'compliance', label: 'Compliance Check' },
    { id: 'actions',    label: `Action Plan (${completedCount}/${mso.actions.length})` },
  ];

  return (
    <div>
      <button onClick={onBack} style={{ ...S.btn('var(--hp-surface)', 'var(--hp-gold)'), marginBottom: 20 }}>← Back to Registry</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <StatusBadge status={mso.status} />
            <ComplianceStatusBadge compliance={compliance} />
            <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{mso.referenceNo}</span>
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', lineHeight: 1.2 }}>{mso.title}</h2>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4 }}>{mso.authority} · {mso.category}</div>
        </div>
        <button onClick={onEdit} style={S.btn('var(--hp-surface)', 'var(--hp-gold)')}><Pencil size={14} /> Edit</button>
      </div>

      <Tabs tabs={DETAIL_TABS} active={detailTab} onChange={setDetailTab} />

      {/* ── Overview tab ── */}
      {detailTab === 'overview' && (
        <div>
          <MSOBanner banner={mso.banner} />
          {/* Clause 4 fuel pass-through control — only on the RTCCO record. The
              Fuel Workbench stages/pushes only while this is ACTIVE. */}
          {('clause4Active' in mso || (mso.referenceNo || '').toUpperCase().startsWith('RTCCO')) && (
            <div style={{ ...S.card, marginBottom: 12, borderLeft: `4px solid ${mso.clause4Active ? 'var(--hp-red)' : 'var(--hp-amber)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div>
                <div style={S.label}>Clause 4 — Fuel Cost Pass-Through</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 12px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', background: mso.clause4Active ? '#450a0a' : '#3f3f46', color: mso.clause4Active ? '#fca5a5' : '#d4d4d8' }}>
                    {mso.clause4Active ? 'ACTIVE' : 'SUSPENDED'}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                    {mso.clause4Active
                      ? 'Obligations live — the Fuel Workbench can stage and push fuel adjustments to RCTIs.'
                      : 'Dormant — the Fuel Workbench is gated off. Reactivate when weekly average TGP diesel returns above $2.00/L.'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  const q = mso.clause4Active
                    ? 'Suspend clause 4? The Fuel Workbench will stop staging/pushing fuel adjustments.'
                    : 'Reactivate clause 4? Confirm the weekly average national TGP diesel price is back above $2.00/L (AIP). The Fuel Workbench unlocks for staging and pushing to RCTIs.';
                  if (window.confirm(q)) onToggleClause4(mso.id);
                }}
                style={S.btn(mso.clause4Active ? 'var(--hp-surface)' : 'var(--hp-green)', mso.clause4Active ? 'var(--hp-amber)' : '#000')}
              >
                {mso.clause4Active ? 'Suspend clause 4' : '⚡ Reactivate clause 4'}
              </button>
            </div>
          )}
          <div style={S.grid3}>
            <div style={S.card}>
              <div style={S.label}>Issued</div>
              <div style={{ color: 'var(--hp-text)', fontSize: '0.9rem' }}>{mso.issuedDate || '—'}</div>
            </div>
            <div style={S.card}>
              <div style={S.label}>Effective</div>
              <div style={{ color: 'var(--hp-text)', fontSize: '0.9rem' }}>{mso.effectiveDate || '—'}</div>
            </div>
            <div style={S.card}>
              <div style={S.label}>Review Due</div>
              <div style={{ color: mso.reviewDate && new Date(mso.reviewDate) < new Date() ? 'var(--hp-red)' : 'var(--hp-text)', fontSize: '0.9rem' }}>{mso.reviewDate || '—'}</div>
            </div>
          </div>
          <div style={{ ...S.card, marginTop: 16 }}>
            <div style={S.label}>Summary</div>
            <p style={{ color: 'var(--hp-text)', fontSize: '0.88rem', lineHeight: 1.7 }}>{mso.summary}</p>
          </div>
          <div style={{ ...S.card, marginTop: 16 }}>
            <div style={{ ...S.row, marginBottom: 14 }}>
              <AlertTriangle size={16} color="var(--hp-amber)" />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Business Impacts</span>
            </div>
            {mso.impacts.length === 0 && <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>No impacts recorded yet.</div>}
            {mso.impacts.map((imp, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < mso.impacts.length - 1 ? '1px solid var(--hp-border)' : 'none' }}>
                <span style={{ color: 'var(--hp-amber)', marginTop: 2, flexShrink: 0 }}>▸</span>
                <span style={{ color: 'var(--hp-text)', fontSize: '0.88rem' }}>{imp}</span>
              </div>
            ))}
          </div>
          {mso.sourceUrl && (
            <div style={{ marginTop: 16 }}>
              <a href={mso.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--hp-blue)', fontSize: '0.82rem', textDecoration: 'none' }}>
                <ExternalLink size={13} /> View official source
              </a>
            </div>
          )}
        </div>
      )}

      {/* ── Compliance check tab ── */}
      {detailTab === 'compliance' && (
        <ComplianceCheckTab
          mso={mso}
          compliance={compliance}
          onChange={c => onUpdateCompliance(mso.id, c)}
        />
      )}

      {/* ── Action plan tab ── */}
      {detailTab === 'actions' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ ...S.row }}>
              <CheckCircle size={16} color="var(--hp-green)" />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Action Plan ({completedCount}/{mso.actions.length} complete)
              </span>
            </div>
            <button onClick={() => setShowAddAction(v => !v)} style={S.btn('var(--hp-surface)', 'var(--hp-gold)')}>
              <Plus size={13} /> Add Action
            </button>
          </div>
          {showAddAction && (
            <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: 12, marginBottom: 12, border: '1px solid var(--hp-gold)' }}>
              <div style={S.grid2}>
                <input style={S.input} value={newAction.description} onChange={e => setNewAction(f => ({ ...f, description: e.target.value }))} placeholder="Action description" />
                <input style={S.input} value={newAction.owner} onChange={e => setNewAction(f => ({ ...f, owner: e.target.value }))} placeholder="Owner / Team" />
              </div>
              <div style={{ ...S.row, marginTop: 8 }}>
                <input type="date" style={{ ...S.input, flex: 1 }} value={newAction.dueDate} onChange={e => setNewAction(f => ({ ...f, dueDate: e.target.value }))} />
                <select style={{ ...S.input, flex: 1 }} value={newAction.status} onChange={e => setNewAction(f => ({ ...f, status: e.target.value }))}>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
                <button onClick={handleAddAction} style={S.btn('var(--hp-green)', '#000')}><Save size={13} /></button>
              </div>
            </div>
          )}
          {mso.actions.length === 0 && !showAddAction && <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>No actions recorded yet.</div>}
          {mso.actions.map(a => <ActionRow key={a.id} action={a} onUpdate={handleUpdateAction} />)}
        </div>
      )}
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab({ msos, onSelect }) {
  const total    = msos.length;
  const watch    = msos.filter(m => m.status === 'pending' || m.status === 'monitoring').length;
  const inForce  = msos.filter(m => ['active', 'implementing', 'suspended'].includes(m.status)).length;
  const outScope = msos.filter(m => m.status === 'out_of_scope').length;
  const banners  = msos.filter(m => m.banner);

  const allActions = msos.flatMap(m => m.actions);
  const overdue = allActions.filter(a => a.status !== 'completed' && a.dueDate && new Date(a.dueDate) < new Date());
  const upcoming = allActions.filter(a => {
    if (a.status === 'completed' || !a.dueDate) return false;
    const d = new Date(a.dueDate);
    const now = new Date();
    const in30 = new Date(); in30.setDate(now.getDate() + 30);
    return d >= now && d <= in30;
  });

  const reviewDue = msos.filter(m => m.reviewDate && new Date(m.reviewDate) <= new Date());

  return (
    <div>
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Tracked" value={total} color="var(--hp-blue)" icon={FileText} />
        <StatCard label="Watch Items" value={watch} color="var(--hp-amber)" icon={TrendingUp} />
        <StatCard label="In Force" value={inForce} color="var(--hp-gold)" icon={AlertTriangle} />
        <StatCard label="Out of Scope" value={outScope} color="var(--hp-dim)" icon={CheckCircle} />
      </div>

      {banners.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {banners.map(m => (
            <MSOBanner key={m.id} banner={{ ...m.banner, text: `${m.referenceNo}: ${m.banner.text}` }} onClick={() => onSelect(m.id)} />
          ))}
        </div>
      )}

      {overdue.length > 0 && (
        <div style={{ background: '#450a0a', border: '1px solid var(--hp-red)', borderRadius: 'var(--radius-lg)', padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <AlertTriangle size={16} color="var(--hp-red)" />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-red)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Overdue Actions ({overdue.length})</span>
          </div>
          {overdue.map(a => {
            const parent = msos.find(m => m.actions.some(x => x.id === a.id));
            return (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(239,68,68,0.2)', fontSize: '0.83rem' }}>
                <span style={{ color: '#fca5a5' }}>{a.description}</span>
                <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{parent?.title?.slice(0, 40)}… · {a.dueDate}</span>
              </div>
            );
          })}
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ background: '#431407', border: '1px solid var(--hp-amber)', borderRadius: 'var(--radius-lg)', padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Clock size={16} color="var(--hp-amber)" />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-amber)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Due Within 30 Days ({upcoming.length})</span>
          </div>
          {upcoming.map(a => {
            const parent = msos.find(m => m.actions.some(x => x.id === a.id));
            return (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(245,158,11,0.2)', fontSize: '0.83rem' }}>
                <span style={{ color: '#fcd34d' }}>{a.description}</span>
                <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{parent?.title?.slice(0, 40)}… · {a.dueDate}</span>
              </div>
            );
          })}
        </div>
      )}

      {reviewDue.length > 0 && (
        <div style={{ background: '#1e1a0f', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius-lg)', padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <RefreshCw size={16} color="var(--hp-gold)" />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>MSOs Due for Review ({reviewDue.length})</span>
          </div>
          {reviewDue.map(m => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(201,168,76,0.2)', fontSize: '0.83rem' }}>
              <button onClick={() => onSelect(m.id)} style={{ background: 'none', border: 'none', color: '#e8c97a', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.83rem', textAlign: 'left', padding: 0 }}>{m.title}</button>
              <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>Review due: {m.reviewDate}</span>
            </div>
          ))}
        </div>
      )}

      <div style={S.card}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>All MSOs — Quick View</div>
        {msos.map(m => {
          const done = m.actions.filter(a => a.status === 'completed').length;
          const pct = m.actions.length ? Math.round((done / m.actions.length) * 100) : 0;
          return (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--hp-border)', cursor: 'pointer' }} onClick={() => onSelect(m.id)}>
              <StatusBadge status={m.status} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.88rem', color: 'var(--hp-text)', fontWeight: 600 }}>{m.title}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{m.authority}</div>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', minWidth: 90, textAlign: 'right' }}>
                {m.actions.length > 0 ? `${pct}% actions done` : 'No actions'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Registry tab ──────────────────────────────────────────────────────────

function RegistryTab({ msos, onSelect, onAdd, onDelete }) {
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  const filtered = msos.filter(m => {
    if (filterStatus && m.status !== filterStatus) return false;
    if (search && !m.title.toLowerCase().includes(search.toLowerCase()) && !m.authority.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <div style={{ ...S.row, marginBottom: 20 }}>
        <input style={{ ...S.input, maxWidth: 280 }} placeholder="Search MSOs..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...S.input, maxWidth: 180 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending — Watch</option>
          <option value="monitoring">Monitoring</option>
          <option value="active">Active</option>
          <option value="implementing">Implementing</option>
          <option value="suspended">Live · Suspended</option>
          <option value="out_of_scope">Out of Scope</option>
          <option value="compliant">Compliant</option>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={onAdd} style={S.btn('var(--hp-gold)', '#000')}><Plus size={14} /> Add MSO</button>
      </div>

      {filtered.length === 0 && <div style={{ color: 'var(--hp-dim)', textAlign: 'center', padding: 40 }}>No MSOs found.</div>}

      {filtered.map(m => {
        const done = m.actions.filter(a => a.status === 'completed').length;
        const overdueCnt = m.actions.filter(a => a.status !== 'completed' && a.dueDate && new Date(a.dueDate) < new Date()).length;
        return (
          <div key={m.id} style={{ ...S.card, marginBottom: 12, cursor: 'pointer' }} onClick={() => onSelect(m.id)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <StatusBadge status={m.status} />
                  <ComplianceStatusBadge compliance={m.compliance} />
                  <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{m.referenceNo}</span>
                  {overdueCnt > 0 && (
                    <span style={{ fontSize: '0.68rem', padding: '1px 7px', borderRadius: 10, background: '#450a0a', color: '#fca5a5', fontWeight: 700 }}>
                      {overdueCnt} overdue
                    </span>
                  )}
                </div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)', marginBottom: 2 }}>{m.title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{m.authority} · {m.category} · Effective: {m.effectiveDate || '—'}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.5 }}>{m.summary?.slice(0, 160)}…</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginLeft: 16 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                  Actions: <strong style={{ color: 'var(--hp-green)' }}>{done}</strong>/{m.actions.length}
                </div>
                <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete this MSO?')) onDelete(m.id); }} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Notifications tab ─────────────────────────────────────────────────────

const NOTIF_STORAGE = 'hp-mso-notif-contacts';

function NotificationsTab({ msos, personnel }) {
  const [contacts, setContacts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTIF_STORAGE) || '[]'); } catch { return []; }
  });
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [selectedMSOs, setSelectedMSOs] = useState([]);
  const [customMsg, setCustomMsg] = useState('');

  useEffect(() => {
    try { localStorage.setItem(NOTIF_STORAGE, JSON.stringify(contacts)); } catch {}
  }, [contacts]);

  const addContact = () => {
    if (!newEmail.trim()) return;
    setContacts(c => [...c, { id: Date.now(), name: newName.trim() || newEmail, email: newEmail.trim(), active: true }]);
    setNewEmail(''); setNewName('');
  };

  const toggleContact = (id) => setContacts(c => c.map(x => x.id === id ? { ...x, active: !x.active } : x));
  const removeContact = (id) => setContacts(c => c.filter(x => x.id !== id));

  const toggleMSOSelect = (id) => setSelectedMSOs(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const buildEmailBody = () => {
    const chosen = msos.filter(m => selectedMSOs.includes(m.id));
    if (chosen.length === 0) return '';
    const lines = chosen.map(m => [
      `MSO: ${m.title}`,
      `Authority: ${m.authority}  |  Status: ${STATUS_CFG[m.status]?.label || m.status}`,
      `Effective: ${m.effectiveDate || '—'}`,
      `Summary: ${m.summary}`,
      m.impacts.length ? `Impacts:\n${m.impacts.map(i => `  • ${i}`).join('\n')}` : '',
      m.actions.filter(a => a.status !== 'completed').length ? `Pending Actions:\n${m.actions.filter(a => a.status !== 'completed').map(a => `  • ${a.description} (${a.owner}, due ${a.dueDate || 'TBD'})`).join('\n')}` : '',
    ].filter(Boolean).join('\n'));
    return (customMsg ? customMsg + '\n\n---\n\n' : '') + lines.join('\n\n---\n\n');
  };

  const handleSendEmail = () => {
    const activeContacts = contacts.filter(c => c.active);
    if (activeContacts.length === 0) { alert('No active contacts selected.'); return; }
    if (selectedMSOs.length === 0) { alert('Select at least one MSO to include.'); return; }
    const to = activeContacts.map(c => c.email).join(',');
    const subject = encodeURIComponent(`HollyPaul MSO Update — ${new Date().toLocaleDateString('en-AU')}`);
    const body = encodeURIComponent(buildEmailBody());
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_blank');
  };

  // Pre-fill from personnel
  const personnelEmails = personnel.filter(p => p.email).map(p => ({ id: 'p-' + p.login, name: p.name || p.login, email: p.email }));

  return (
    <div>
      <div style={S.grid2}>
        <div>
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Notification Contacts</div>

            <div style={{ ...S.row, marginBottom: 16 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="Name" value={newName} onChange={e => setNewName(e.target.value)} />
              <input style={{ ...S.input, flex: 2 }} placeholder="Email address" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
              <button onClick={addContact} style={S.btn('var(--hp-gold)', '#000')}><Plus size={13} /></button>
            </div>

            {contacts.length === 0 && <div style={{ color: 'var(--hp-dim)', fontSize: '0.83rem', marginBottom: 12 }}>No contacts added yet.</div>}
            {contacts.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--hp-border)' }}>
                <input type="checkbox" checked={c.active} onChange={() => toggleContact(c.id)} style={{ cursor: 'pointer' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', color: c.active ? 'var(--hp-text)' : 'var(--hp-dim)' }}>{c.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{c.email}</div>
                </div>
                <button onClick={() => removeContact(c.id)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><X size={13} /></button>
              </div>
            ))}

            {personnelEmails.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginBottom: 8, fontFamily: 'var(--font-mono)' }}>Quick-add from Personnel</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {personnelEmails.filter(p => !contacts.some(c => c.email === p.email)).map(p => (
                    <button key={p.id} onClick={() => setContacts(c => [...c, { ...p, id: Date.now(), active: true }])} style={S.btn('var(--hp-surface)', 'var(--hp-gold)')}>
                      + {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Compose Notification</div>

            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Select MSOs to include</label>
              {msos.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--hp-border)' }}>
                  <input type="checkbox" checked={selectedMSOs.includes(m.id)} onChange={() => toggleMSOSelect(m.id)} style={{ cursor: 'pointer' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.83rem', color: 'var(--hp-text)' }}>{m.title}</div>
                  </div>
                  <StatusBadge status={m.status} />
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Custom message (optional)</label>
              <textarea rows={4} style={S.textarea} value={customMsg} onChange={e => setCustomMsg(e.target.value)} placeholder="Add a personal note to the notification email..." />
            </div>

            <button onClick={handleSendEmail} style={{ ...S.btn('var(--hp-gold)', '#000'), width: '100%', justifyContent: 'center' }}>
              <Mail size={15} /> Send Notification Email
            </button>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8, textAlign: 'center' }}>Opens your default email client with all details pre-filled</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── References tab ────────────────────────────────────────────────────────

const REF_CATEGORIES = ['Legislation', 'Regulatory', 'Transport Safety', 'WHS', 'Industry', 'State Authority', 'Tax & Super', 'Other'];

function ReferencesTab() {
  const [refs, setRefs] = useState(loadRefs);
  const [filterCat, setFilterCat] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', category: 'Legislation', description: '' });

  useEffect(() => { saveRefs(refs); }, [refs]);

  const addRef = () => {
    if (!form.name.trim() || !form.url.trim()) return;
    setRefs(r => [...r, { ...form, id: 'ref-' + Date.now() }]);
    setForm({ name: '', url: '', category: 'Legislation', description: '' });
    setAdding(false);
  };

  const removeRef = (id) => setRefs(r => r.filter(x => x.id !== id));

  const filtered = filterCat ? refs.filter(r => r.category === filterCat) : refs;
  const categories = [...new Set(refs.map(r => r.category))].sort();

  const CAT_COLOR = {
    'Legislation':     '#3b82f6',
    'Regulatory':      '#8b5cf6',
    'Transport Safety':'#f59e0b',
    'WHS':             '#ef4444',
    'Industry':        '#22c55e',
    'State Authority': '#06b6d4',
    'Tax & Super':     '#f97316',
    'Other':           '#6b7280',
  };

  return (
    <div>
      <div style={{ ...S.row, marginBottom: 20 }}>
        <select style={{ ...S.input, maxWidth: 200 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={() => setAdding(v => !v)} style={S.btn('var(--hp-gold)', '#000')}><Plus size={14} /> Add Link</button>
      </div>

      {adding && (
        <div style={{ ...S.card, marginBottom: 20, border: '1px solid var(--hp-gold)' }}>
          <div style={S.grid2}>
            <div style={S.section}>
              <label style={S.label}>Name</label>
              <input style={S.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Link name" />
            </div>
            <div style={S.section}>
              <label style={S.label}>Category</label>
              <select style={S.input} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {REF_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={S.section}>
            <label style={S.label}>URL</label>
            <input style={S.input} value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." />
          </div>
          <div style={S.section}>
            <label style={S.label}>Description</label>
            <input style={S.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={S.btn('var(--hp-surface)', 'var(--hp-dim)')}>Cancel</button>
            <button onClick={addRef} style={S.btn('var(--hp-gold)', '#000')}><Save size={13} /> Save</button>
          </div>
        </div>
      )}

      {Object.entries(
        filtered.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {})
      ).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: CAT_COLOR[cat] || '#6b7280', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: CAT_COLOR[cat] || 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>{cat}</span>
            <span style={{ fontSize: '0.68rem', color: 'var(--hp-dim)' }}>({items.length})</span>
          </div>
          {items.map(r => (
            <div key={r.id} style={{ ...S.card, marginBottom: 8, borderLeft: `3px solid ${CAT_COLOR[r.category] || '#6b7280'}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--hp-gold)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {r.name} <ExternalLink size={11} />
                  </a>
                  {r.description && <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>{r.description}</div>}
                  <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)', marginTop: 3, fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{r.url}</div>
                </div>
                <button onClick={() => removeRef(r.id)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', flexShrink: 0 }}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── History tab ───────────────────────────────────────────────────────────

const HISTORY_TYPE_CFG = {
  mso_added:       { label: 'MSO Added',        color: '#22c55e', icon: '＋' },
  mso_updated:     { label: 'MSO Updated',       color: '#3b82f6', icon: '✎' },
  mso_deleted:     { label: 'MSO Removed',       color: '#ef4444', icon: '✕' },
  status_changed:  { label: 'Status Changed',    color: '#f59e0b', icon: '⇄' },
  action_updated:  { label: 'Action Updated',    color: '#8b5cf6', icon: '✓' },
  search_performed:{ label: 'Search Performed',  color: '#06b6d4', icon: '⌕' },
};

function HistoryTab({ history }) {
  const [filterType, setFilterType] = useState('');

  const filtered = filterType ? history.filter(h => h.type === filterType) : history;

  const fmtTs = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div style={{ ...S.row, marginBottom: 20 }}>
        <select style={{ ...S.input, maxWidth: 220 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All event types</option>
          {Object.entries(HISTORY_TYPE_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{filtered.length} event{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      {filtered.length === 0 && (
        <div style={{ ...S.card, textAlign: 'center', padding: 48, color: 'var(--hp-dim)' }}>
          <History size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: '0.88rem' }}>No history yet. Events are recorded as you add MSOs, update actions, and run searches.</div>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {filtered.length > 0 && (
          <div style={{ position: 'absolute', left: 19, top: 0, bottom: 0, width: 2, background: 'var(--hp-border)' }} />
        )}
        {filtered.map((evt, i) => {
          const cfg = HISTORY_TYPE_CFG[evt.type] || { label: evt.type, color: 'var(--hp-dim)', icon: '·' };
          return (
            <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 14, position: 'relative' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: cfg.color + '22', border: `2px solid ${cfg.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', color: cfg.color, flexShrink: 0, zIndex: 1 }}>
                {cfg.icon}
              </div>
              <div style={{ ...S.card, flex: 1, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cfg.label}</span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{fmtTs(evt.ts)}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--hp-text)' }}>{evt.description}</div>
                {evt.detail && <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 3 }}>{evt.detail}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

// ── Sync status indicator ──────────────────────────────────────────────────

function SyncBadge({ status, onRetry }) {
  const cfgs = {
    loading: { icon: <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />, label: 'Loading…',     color: 'var(--hp-dim)' },
    saving:  { icon: <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />, label: 'Saving…',      color: 'var(--hp-amber)' },
    synced:  { icon: <Cloud  size={13} />,                                                   label: 'Saved to cloud',  color: 'var(--hp-green)' },
    error:   { icon: <CloudOff size={13} />,                                                 label: 'Save failed',  color: 'var(--hp-red)' },
    offline: { icon: <CloudOff size={13} />,                                                 label: 'Offline — using local storage', color: 'var(--hp-dim)' },
  };
  const cfg = cfgs[status] || cfgs.offline;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', color: cfg.color, fontFamily: 'var(--font-mono)' }}>
      {cfg.icon} {cfg.label}
      {status === 'error' && (
        <button onClick={onRetry} style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'var(--font-mono)', textDecoration: 'underline', padding: 0 }}>Retry</button>
      )}
    </div>
  );
}

export default function MSOPage() {
  const { personnel, addNotification } = useApp();
  const [msos, setMSOs] = useState(loadMSOs);
  const [tab, setTab] = useState('overview');
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [history, setHistory] = useState(loadHistory);
  const [lastSearched, setLastSearched] = useState(() => localStorage.getItem('hp-mso-last-search') || null);
  const [syncStatus, setSyncStatus] = useState('loading');
  const initialised = useRef(false);
  const saveTimer = useRef(null);

  // ── On mount: load from Sheets (fall back to localStorage) ──
  useEffect(() => {
    (async () => {
      const sheetsMSOs = await loadMSOsFromSheets();
      if (sheetsMSOs && sheetsMSOs.length > 0) {
        // Sheets is authoritative — use it
        setMSOs(sheetsMSOs);
        saveMSOs(sheetsMSOs);         // keep localStorage in sync as cache
        setSyncStatus('synced');
      } else if (sheetsMSOs && sheetsMSOs.length === 0) {
        // Sheet exists but empty — migrate localStorage data up to Sheets
        const local = loadMSOs();
        if (local.length > 0) {
          const ok = await saveMSOsToSheets(local);
          setSyncStatus(ok ? 'synced' : 'offline');
        } else {
          setSyncStatus('synced');
        }
      } else {
        // Network/script error — fall back to localStorage
        setSyncStatus('offline');
      }
      initialised.current = true;
    })();
  }, []);

  // ── Auto-save to Sheets whenever msos changes (after initial load) ──
  const doSheetSave = useCallback(async (data) => {
    setSyncStatus('saving');
    const ok = await saveMSOsToSheets(data);
    setSyncStatus(ok ? 'synced' : 'error');
  }, []);

  useEffect(() => {
    if (!initialised.current) return;   // skip during initial load
    saveMSOs(msos);                     // always keep localStorage in sync
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSheetSave(msos), 1500);
    return () => clearTimeout(saveTimer.current);
  }, [msos, doSheetSave]);

  const logHistory = (entry) => {
    pushHistory(entry);
    setHistory(loadHistory());
  };

  const selectedMSO = msos.find(m => m.id === selectedId);

  const handleSaveMSO = (mso) => {
    const isNew = !msos.find(m => m.id === mso.id);
    setMSOs(list => isNew ? [...list, mso] : list.map(m => m.id === mso.id ? mso : m));
    logHistory({
      type: isNew ? 'mso_added' : 'mso_updated',
      description: isNew ? `New MSO added: "${mso.title}"` : `MSO updated: "${mso.title}"`,
      detail: `${mso.authority} · ${mso.category} · Status: ${STATUS_CFG[mso.status]?.label || mso.status}`,
    });
    addNotification(isNew ? 'MSO added' : 'MSO updated', 'success');
  };

  const handleDelete = (id) => {
    const mso = msos.find(m => m.id === id);
    setMSOs(list => list.filter(m => m.id !== id));
    logHistory({ type: 'mso_deleted', description: `MSO removed: "${mso?.title}"` });
    addNotification('MSO removed', 'info');
  };

  // RTCCO clause 4 (fuel pass-through) toggle. The Fuel Workbench reads this flag
  // via isClause4Active() from localStorage 'hp-msos' — flipping it here hits
  // localStorage immediately (saveMSOs in the msos effect) and syncs to Sheets
  // through the debounced auto-save, so it survives the Sheets-authoritative load.
  const handleToggleClause4 = (msoId) => {
    const mso = msos.find(m => m.id === msoId);
    const activating = mso?.clause4Active !== true;
    setMSOs(list => list.map(m => m.id === msoId ? {
      ...m,
      clause4Active: activating,
      status: activating ? 'active' : 'suspended',
      banner: activating
        ? { tone: 'red',   text: 'Clause 4 fuel obligations ACTIVE — weekly average TGP diesel back above $2.00/L. Calculate the fortnightly pass-through in the Fuel Workbench and push to RCTIs.' }
        : { tone: 'amber', text: 'Clause 4 fuel obligations SUSPENDED (diesel below $2.00/L). Monitoring continues; do not remove pass-through logic.' },
    } : m));
    logHistory({
      type: 'status_changed',
      description: activating ? 'RTCCO clause 4 REACTIVATED — fuel pass-through obligations live' : 'RTCCO clause 4 suspended — fuel pass-through dormant',
      detail: mso?.title,
    });
    addNotification(activating ? 'Clause 4 reactivated — Fuel Workbench unlocked' : 'Clause 4 suspended — Fuel Workbench gated off', 'success');
  };

  const handleUpdateCompliance = (msoId, compliance) => {
    const mso = msos.find(m => m.id === msoId);
    const prev = mso?.compliance;
    setMSOs(list => list.map(m => m.id === msoId ? { ...m, compliance } : m));
    const outcome = getComplianceOutcome(compliance);
    const prevOutcome = getComplianceOutcome(prev);
    if (outcome !== prevOutcome) {
      const cfg = COMP_OUTCOME_CFG[outcome] || COMP_OUTCOME_CFG.pending;
      logHistory({ type: 'status_changed', description: `Compliance assessment updated: "${mso?.title}"`, detail: cfg.label });
    }
  };

  const handleUpdateActions = (msoId, actions) => {
    const mso = msos.find(m => m.id === msoId);
    const prevActions = mso?.actions || [];
    setMSOs(list => list.map(m => m.id === msoId ? { ...m, actions } : m));
    // Log if an action was just completed
    actions.forEach(a => {
      const prev = prevActions.find(p => p.id === a.id);
      if (prev && prev.status !== 'completed' && a.status === 'completed') {
        logHistory({ type: 'action_updated', description: `Action completed: "${a.description}"`, detail: `MSO: ${mso?.title}` });
      } else if (prev && prev.status !== a.status) {
        logHistory({ type: 'action_updated', description: `Action status → ${a.status}: "${a.description}"`, detail: `MSO: ${mso?.title}` });
      }
    });
  };

  const handleSearchDone = () => {
    const ts = new Date().toISOString();
    setLastSearched(ts);
    localStorage.setItem('hp-mso-last-search', ts);
    logHistory({ type: 'search_performed', description: `Manual MSO search performed — ${SEARCH_SOURCES.length} sources opened`, detail: SEARCH_SOURCES.map(s => s.name).join(', ') });
  };

  const fmtLastSearched = () => {
    if (!lastSearched) return null;
    const d = new Date(lastSearched);
    return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = () => { setEditTarget(selectedMSO); setModalOpen(true); };

  const TABS = [
    { id: 'overview',      label: 'Overview' },
    { id: 'registry',      label: 'MSO Registry' },
    { id: 'references',    label: 'References' },
    { id: 'history',       label: `History${history.length ? ` (${history.length})` : ''}` },
    { id: 'notifications', label: 'Notifications' },
  ];

  return (
    <div style={S.page}>
      <div style={{ ...S.header, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={S.title}>MSO TRACKER</h1>
          <div style={S.sub}>Fair Work Commission road transport instruments — compliance watch (as at 10 Jul 2026)</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button onClick={() => setSearchOpen(true)} style={{ ...S.btn('var(--hp-gold)', '#000'), fontSize: '0.88rem', padding: '10px 20px' }}>
            <Search size={15} /> Search for New MSOs
          </button>
          <SyncBadge status={syncStatus} onRetry={() => doSheetSave(msos)} />
          {lastSearched && (
            <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>
              Last searched: {fmtLastSearched()}
            </div>
          )}
        </div>
      </div>

      {selectedId && selectedMSO ? (
        <MSODetail
          mso={selectedMSO}
          onBack={() => setSelectedId(null)}
          onEdit={openEdit}
          onUpdateActions={handleUpdateActions}
          onUpdateCompliance={handleUpdateCompliance}
          onToggleClause4={handleToggleClause4}
        />
      ) : (
        <>
          <Tabs tabs={TABS} active={tab} onChange={setTab} />
          {tab === 'overview'      && <OverviewTab msos={msos} onSelect={id => setSelectedId(id)} />}
          {tab === 'registry'      && <RegistryTab msos={msos} onSelect={id => setSelectedId(id)} onAdd={openAdd} onDelete={handleDelete} />}
          {tab === 'references'    && <ReferencesTab />}
          {tab === 'history'       && <HistoryTab history={history} />}
          {tab === 'notifications' && <NotificationsTab msos={msos} personnel={personnel} />}
        </>
      )}

      {searchOpen && (
        <SearchModal onClose={() => setSearchOpen(false)} onSearchDone={handleSearchDone} />
      )}

      {modalOpen && (
        <MSOModal
          mso={editTarget}
          onSave={handleSaveMSO}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
        />
      )}
    </div>
  );
}
